import type { InstallableModel, SpeechModel, VoiceManifest } from '../types/LocalAiTypes';
import { isSpeechModelKey, speechModelKeys } from '../stt/SpeechModelSelection';
import { clearLocalModelCache, isModelCached } from './ModelCache';
import { resolveModel } from './ModelWeights';
import { downloadModels, type ModelDownloadProgress } from './ModelDownloader';

export interface LocalModelStatus {
  basicInstalled: boolean;
  enhancedInstalled: boolean;
  installed: readonly string[];
  /** Which of the two speech checkpoints are on this device, whichever one is selected. */
  speech: Readonly<Record<SpeechModel, boolean>>;
}

export class LocalModelManager {
  /**
   * @param halfPrecision Whether the GPU compiles fp16 shaders — see `supportsHalfPrecision`.
   * It decides which of a model's published weight formats is the one this device installs,
   * so it belongs to the manager rather than to any single call: what is downloaded, what
   * counts as installed, and what the size beside the button says all have to agree.
   */
  public constructor(
    private readonly manifest: VoiceManifest,
    private readonly halfPrecision: boolean,
  ) {}

  /**
   * The models a package comes to on this device.
   *
   * Two speech checkpoints are published and exactly one is ever fetched, so the selection is
   * an argument rather than a property of the package: the size shown beside the install button
   * has to be the size that actually arrives, and downloading 780 MB to use 537 MB of it would
   * be a lie told in the one place an operator is watching the number.
   */
  public modelsFor(packageName: 'basic' | 'enhanced', speechModel: SpeechModel): readonly InstallableModel[] {
    const selected = speechModelKeys[speechModel];
    return this.manifest.models.models
      .filter((model) => model.enabled
        && (model.package === 'basic' || packageName === 'enhanced')
        && (!isSpeechModelKey(model.key) || model.key === selected))
      /*
       * A model this device has no runnable format for is left out rather than allowed to
       * throw. It is not an error for a package to contain one — that is the whole reason more
       * than one format is published — and an operator cannot act on a install button that
       * refuses to render.
       */
      .flatMap((model) => {
        try {
          return [resolveModel(model, this.halfPrecision)];
        } catch {
          return [];
        }
      });
  }

  public packageBytes(packageName: 'basic' | 'enhanced', speechModel: SpeechModel): number {
    return this.modelsFor(packageName, speechModel).reduce((total, model) => total + model.totalBytes, 0);
  }

  /** What one speech checkpoint costs here — its own files plus the weights chosen for them. */
  public speechBytes(speechModel: SpeechModel): number {
    const model = this.manifest.models.models.find((item) => item.key === speechModelKeys[speechModel]);
    if (!model) return 0;
    try {
      return resolveModel(model, this.halfPrecision).totalBytes;
    } catch {
      // Published, but in no format this device can run. Nothing to price.
      return 0;
    }
  }

  public async status(speechModel: SpeechModel): Promise<LocalModelStatus> {
    const published = this.manifest.models.models.filter((model) => model.enabled).flatMap((model) => {
      try {
        return [resolveModel(model, this.halfPrecision)];
      } catch {
        return [];
      }
    });
    const installed = (await Promise.all(published.map(async (model) => ({
      key: model.key,
      id: model.id,
      installed: await isModelCached(model),
    })))).filter((item) => item.installed);
    const installedIds = installed.map((item) => item.id);
    const installedKeys = new Set(installed.map((item) => item.key));
    const enhancedOnly = published.filter((model) => model.package === 'enhanced');
    return {
      basicInstalled: this.modelsFor('basic', speechModel).every((model) => installedIds.includes(model.id)),
      enhancedInstalled: enhancedOnly.length > 0 && enhancedOnly.every((model) => installedIds.includes(model.id)),
      installed: installedIds,
      speech: {
        turbo: installedKeys.has(speechModelKeys.turbo),
        small: installedKeys.has(speechModelKeys.small),
      },
    };
  }

  public install(
    packageName: 'basic' | 'enhanced',
    speechModel: SpeechModel,
    onProgress?: (progress: ModelDownloadProgress) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return downloadModels(this.modelsFor(packageName, speechModel), onProgress, signal);
  }

  public clear(): Promise<void> {
    return clearLocalModelCache();
  }
}
